import { ArrowForward, HighlightAlt } from "@mui/icons-material"
import { Box, IconButton, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material"
import { useContext, useEffect, useMemo, useState } from "react"
import { connect } from "react-redux"
import { selectTags } from "../../../redux/selectors/firebase"
import { FirebaseContext } from "../../firebase/FirebaseProvider"
import { Tags } from "../../graph/StateTags"

export interface OrderConstraintValues
{
    data: { values: (string | number | boolean)[] }
}

interface Props
{
    admin: boolean
    project: string
    constant: string
    constraint: OrderConstraintValues
    stateFilter?: string[]
    id: string
    tags: Tags
    focusSequenceValue?: (value: string | number | boolean) => void
    selectSequenceOfValue?: (value: string | number | boolean) => void
}

function OrderableData({ tags, admin, project, stateFilter,
    constant, constraint, id, focusSequenceValue, selectSequenceOfValue }: Props)
{
    const [order, setOrder] = useState<(string | number | boolean)[]>([]);
    const [drop, setDrop] = useState(false);

    const { set } = useContext(FirebaseContext);
    const uniqueValues: any[] = useMemo(() => Array.from(Object.values(tags).reduce((values, stateTags) =>
    {
        Object.values(stateTags).forEach((tag) =>
        {
            if (tag.type === constant)
            {
                values.add(tag.value);
            }
        });
        return values;
    }, new Set<any>())), [tags, constant]);

    const filteredTagValues = useMemo(() =>
    {
        if (stateFilter)
        {
            return Array.from(Object.entries(tags).reduce((values, [stateId, stateTags]) =>
            {
                if (stateFilter.includes(stateId))
                {
                    Object.values(stateTags).forEach((tag) =>
                    {
                        if (tag.type === constant)
                        {
                            values.add(tag.value);
                        }
                    });
                }
                return values;
            }, new Set<any>()))
        }

        return null;
    }, [constant, stateFilter, tags]);

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) =>
    {
        event.preventDefault();
    }

    const handleDragStart = (event: React.DragEvent<HTMLDivElement>) =>
    {
        event.dataTransfer.setData("source", event.currentTarget.dataset.id ?? "");
    }

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) =>
    {
        event.preventDefault();
        const source = Number(event.dataTransfer.getData("source"));
        const target = Number(event.currentTarget.dataset.id);

        setDrop(true);
        setOrder((ordering) =>
        {
            const value = ordering[source];
            ordering.splice(source, 1);
            ordering.splice(target, 0, value);
            return [...ordering];
        });
    }

    useEffect(() =>
    {
        const orderConstraint = Array.from(constraint.data?.values ?? []);

        uniqueValues.forEach((value) =>
        {
            if (!orderConstraint.includes(value))
            {
                orderConstraint.push(value);
            }
        });

        setOrder(orderConstraint);
    }, [constraint, uniqueValues]);

    useEffect(() =>
    {
        if (drop)
        {
            order.forEach((value, index) =>
            {
                if (!constraint.data || constraint.data?.values[index] !== value)
                {
                    set(`constants/${project}/${constant}/constraints/${id}/data/values/${index}`, value);
                }
            });
            setDrop(false);
        }

    }, [drop, order, constant, constraint.data, id, project, set]);

    const showActions = focusSequenceValue || selectSequenceOfValue;

    return <Box>
        <Table size={"small"}>
            <TableHead>
                <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Tag Value</TableCell>
                    {showActions && <TableCell>Actions</TableCell>}
                </TableRow>
            </TableHead>
            <TableBody>
                {order.map((tagValue, index) =>
                {
                    return !filteredTagValues || filteredTagValues.includes(tagValue) ? <TableRow key={index}
                        sx={{ cursor: admin ? "grab" : "default" }}
                        data-id={index}
                        draggable={admin}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragStart={handleDragStart}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{tagValue}</TableCell>
                        {showActions && <TableCell>
                            {focusSequenceValue && <IconButton size={"small"} onClick={() => focusSequenceValue(tagValue)}><ArrowForward fontSize="small" /></IconButton>}
                            {selectSequenceOfValue && <IconButton size={"small"} onClick={() => selectSequenceOfValue(tagValue)}><HighlightAlt fontSize="small" /></IconButton>}
                        </TableCell>}
                    </TableRow> : null
                })}
            </TableBody>
        </Table>
    </Box>
}

const mapStateToProps = (store: never) => ({
    tags: selectTags(store) ?? {}
});
export default connect(mapStateToProps, {
})(OrderableData);